import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { closetAPI, getFullImageUrl } from '../utils/api';
import '../styles/Closet.css';

// Icons
import SearchIcon from '@mui/icons-material/SearchRounded';
import AddIcon from '@mui/icons-material/AddRounded';
import CameraAltIcon from '@mui/icons-material/CameraAltRounded';
import FilterIcon from '@mui/icons-material/FilterListRounded';
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded';
import RefreshIcon from '@mui/icons-material/RefreshRounded';
import CheckroomIcon from '@mui/icons-material/CheckroomRounded';

const Closet = () => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [sourcePhotos, setSourcePhotos] = useState([]);
  const [sourceRetentionDays, setSourceRetentionDays] = useState(14);

  const fetchItems = useCallback(async () => {
    try {
      const response = await closetAPI.getItems();
      const data = response?.data?.data?.items || response?.data?.items || [];
      setItems(data);
      setFilteredItems(data);
      try {
        const sourceResponse = await closetAPI.getSourcePhotos();
        const sourceData = sourceResponse?.data?.data || {};
        setSourcePhotos(sourceData.source_photos || []);
        setSourceRetentionDays(sourceData.retention_days || 14);
      } catch (sourceError) {
        console.error('Error fetching temporary scan photos:', sourceError);
      }
    } catch (error) {
      console.error('Error fetching closet:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    let result = items;
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(i =>
        (i.name && i.name.toLowerCase().includes(lower)) ||
        (i.category && i.category.toLowerCase().includes(lower))
      );
    }
    if (categoryFilter) {
      result = result.filter(i => i.category === categoryFilter);
    }
    setFilteredItems(result);
  }, [items, searchTerm, categoryFilter]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await closetAPI.deleteItem(id);
        setItems(prev => prev.filter(i => i.id !== id));
      } catch (err) {
        console.error('Failed to delete', err);
      }
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchItems();
  };

  const handleDeleteSourcePhoto = async (sourcePhoto) => {
    const confirmed = window.confirm(
      'Delete this original scan photo now? Saved closet items and reviewed garment crops will remain.'
    );
    if (!confirmed) return;
    try {
      await closetAPI.deleteSourcePhoto(sourcePhoto.id);
      setSourcePhotos(prev => prev.filter(photo => photo.id !== sourcePhoto.id));
    } catch (error) {
      console.error('Failed to delete original scan photo:', error);
    }
  };

  const formatExpiry = (value) => {
    if (!value) return 'soon';
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="closet-container animate-fade-in">
      {/* Header */}
      <div className="closet-header">
        <div className="closet-title-section">
          <div>
            <h1 className="closet-title">My Closet</h1>
            <p className="text-secondary">Manage and organize your wardrobe</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Link to="/closet/scan" className="btn-primary btn-scan" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CameraAltIcon fontSize="small" />
              Scan My Closet
            </Link>
            <Link to="/closet/add" className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AddIcon fontSize="small" />
              Add New Item
            </Link>
          </div>
        </div>

        {/* Controls */}
        <div className="glass-panel closet-controls">
          <div className="search-wrapper">
            <SearchIcon className="search-icon" />
            <input
              type="text"
              placeholder="Search items..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FilterIcon className="text-secondary" />
            <select
              className="filter-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              <option value="Tops">Tops</option>
              <option value="Bottoms">Bottoms</option>
              <option value="Dresses">Dresses</option>
              <option value="Outerwear">Outerwear</option>
              <option value="Shoes">Shoes</option>
              <option value="Accessories">Accessories</option>
            </select>
          </div>

          <button
            className="btn-ghost"
            onClick={handleRefresh}
            disabled={refreshing}
            style={{ padding: '8px', minWidth: 'auto' }}
            title="Refresh"
          >
            <RefreshIcon className={refreshing ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {sourcePhotos.length > 0 && (
        <section className="glass-panel scan-privacy-panel" aria-labelledby="scan-photo-recovery-title">
          <div>
            <h2 id="scan-photo-recovery-title">Original scan photo recovery</h2>
            <p className="text-secondary">
              {`Originals are kept for up to ${sourceRetentionDays} days, then deleted automatically. Your reviewed garment crops stay with saved closet items.`}
            </p>
          </div>
          <div className="scan-source-list">
            {sourcePhotos.map((photo) => (
              <div className="scan-source-row" key={photo.id}>
                <div>
                  <strong>{photo.original_filename || 'Closet scan photo'}</strong>
                  <span>{`Deletes automatically ${formatExpiry(photo.expires_at)}`}</span>
                </div>
                <button
                  className="btn-ghost scan-source-delete"
                  onClick={() => handleDeleteSourcePhoto(photo)}
                  type="button"
                >
                  <DeleteIcon fontSize="small" />
                  Delete original now
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Grid Content */}
      {loading ? (
        <div className="closet-skeleton-grid">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="glass-panel closet-skeleton-card">
              <div className="loading-pulse closet-skeleton-image" />
              <div className="closet-skeleton-content">
                <div className="loading-pulse closet-skeleton-line" />
                <div className="loading-pulse closet-skeleton-line short" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="glass-panel empty-state">
          <CheckroomIcon className="empty-icon" />
          {searchTerm || categoryFilter ? (
            <>
              <h3>No items found</h3>
              <p className="text-secondary">Try adjusting your search or filters.</p>
            </>
          ) : (
            <>
              <h3>Your closet is empty</h3>
              <p className="text-secondary">Start building your digital wardrobe. Choose how to add your first items.</p>
              <div className="empty-state-paths">
                <Link to="/closet/add" className="empty-path-card">
                  <AddIcon className="empty-path-icon" />
                  <div>
                    <p className="empty-path-title">Add an Item</p>
                    <p className="empty-path-desc">Describe or upload a single piece</p>
                  </div>
                </Link>
                <Link to="/closet/scan" className="empty-path-card">
                  <CameraAltIcon className="empty-path-icon" />
                  <div>
                    <p className="empty-path-title">Scan My Closet</p>
                    <p className="empty-path-desc">Upload photos, AI detects everything</p>
                  </div>
                </Link>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="closet-grid">
          {filteredItems.map((item) => (
            <div key={item.id} className="glass-panel closet-item-card">
              <img
                src={getFullImageUrl(item.image_url) || 'https://via.placeholder.com/300?text=No+Image'}
                alt={item.name || item.category}
                className="item-image"
                loading="lazy"
              />
              <div className="item-overlay">
                <div className="item-title">{item.name || item.category}</div>
                <div className="item-category">{item.category} • {item.color || 'No Color'}</div>
              </div>
              <div className="item-actions">
                <button
                  className="action-icon-btn"
                  onClick={(e) => handleDelete(e, item.id)}
                  title="Delete Item"
                >
                  <DeleteIcon fontSize="small" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Closet;
